import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import type { IPFamily, IPInfo } from '../shared/types'
import { ArcadeScene } from './scene'
import { CrtScreen, type CrtPhase } from './crt'
import { audio } from './audio'
import { fetchInfo, fetchOtherFamily, speedTest, type SpeedResult } from './api'
import { useLive } from './useLive'
import { useKonami } from './konami'
import { GameRunner, type GameKind, type RunnerStatus } from './games/runner'
import type { Dash } from './games/dash'
import type { Cell } from './games/types'

type Modal = null | 'json' | 'help'
interface Toast { id: number; text: string; big?: boolean }

const IDLE_MS = 90_000
const DEMO_ORDER: GameKind[] = ['snake', 'invaders', 'dash']
const DEMO_MAX_MS: Record<GameKind, number> = { snake: 40_000, invaders: 45_000, dash: 30_000 }
const GAME_LABEL: Record<GameKind, string> = { snake: 'Snake', invaders: 'Invaders', dash: 'Bonus Stage' }
const GAME_HINT: Record<GameKind, string> = { snake: 'arrows or swipe to steer', invaders: '← → move · space or tap to fire', dash: '↑ space or tap to jump' }
/** The CRT melts into the middle of the page, then the tube warms up; the game holds still until the picture is on. */
const CRT_MELT_MS = 900
const CRT_WARM_MS = 400
const CRT_OUT_MS = 1000
const CRT_TIMINGS = { melt: CRT_MELT_MS, warm: CRT_WARM_MS, out: CRT_OUT_MS }
const REDUCE_MOTION = matchMedia('(prefers-reduced-motion: reduce)').matches

interface CrtState { kind: GameKind; W: number; H: number; phase: CrtPhase }
interface Frame { cells: Cell[]; W: number; H: number }
/** The bonus stage's speed test, as shown on the speedo under the screen. */
type Speed =
	| { phase: 'ping' }
	| { phase: 'download'; fraction: number; mbps: number }
	| { phase: 'done'; result: SpeedResult; record: boolean }
	| { phase: 'failed' }
const SPEED_BYTES = 8 * 1024 * 1024

const pad2 = (n: number) => String(n).padStart(2, '0')
const up = (s: string | undefined | null, fallback = '???') => (s ? s.toUpperCase() : fallback)
const flag = (cc?: string) => (cc && /^[A-Z]{2}$/i.test(cc) ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0))) : '')
const readNum = (key: string) => { try { return Number(localStorage.getItem(key) ?? 0) || 0 } catch { return 0 } }
const writeNum = (key: string, v: number) => { try { localStorage.setItem(key, String(v)) } catch { /* ignore */ } }

/** The IP as a rainbow sine scroller. Rises from the screen centre (where the voxels were) to sit under the logo. */
function IPLine({ ip }: { ip: string }) {
	const ref = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const el = ref.current
		if (!el) return
		const r = el.getBoundingClientRect()
		el.style.setProperty('--rise', `${Math.max(0, innerHeight / 2 - (r.top + r.height / 2))}px`)
		el.classList.add('rise')
	}, [])
	return (
		<div ref={ref} className={`ipline${ip.length > 20 ? ' long' : ''}`} aria-hidden="true">
			{[...ip].map((ch, i) => <span key={i} style={{ animationDelay: `${0.7 + i * 0.06}s, ${i * -0.15}s` }}>{ch}</span>)}
		</div>
	)
}

/** The play area: a curved sheet of CRT glass the runner draws into. The shader does the melt. */
function Crt({ kind, W, H, phase, attach }: CrtState & { attach: (screen: CrtScreen | null) => void }) {
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const screenRef = useRef<CrtScreen | null>(null)
	useEffect(() => {
		if (!canvasRef.current) return
		const screen = new CrtScreen(canvasRef.current, CRT_TIMINGS)
		screenRef.current = screen
		attach(screen)
		return () => { attach(null); screen.dispose(); screenRef.current = null }
	}, [attach])
	useEffect(() => { screenRef.current?.setPhase(phase) }, [phase])
	return (
		<div className="crt" style={{ '--ar': `${W} / ${H}` } as CSSProperties}>
			<canvas ref={canvasRef} aria-label={`${GAME_LABEL[kind]} play area`} />
		</div>
	)
}

/** The dyno readout for the speed test: what it's doing, a progress bar and the live Mbps. */
function Speedo({ speed }: { speed: Speed }) {
	const running = speed.phase === 'ping' || speed.phase === 'download'
	const mbps = speed.phase === 'download' ? speed.mbps : speed.phase === 'done' ? speed.result.mbps : 0
	const fraction = speed.phase === 'download' ? speed.fraction : speed.phase === 'done' ? 1 : 0
	const label =
		speed.phase === 'ping' ? 'Speed test · measuring ping…'
		: speed.phase === 'download' ? `Speed test · downloading ${Math.round(SPEED_BYTES / 1048576)} MB from the edge · ${Math.round(fraction * 100)}%`
		: speed.phase === 'done' ? `Speed test complete · ${speed.result.pingMs} ms ping · ${(speed.result.bytes / 1048576).toFixed(0)} MB in ${speed.result.seconds.toFixed(1)} s`
		: 'Speed test failed · running on fumes'
	const turbo = speed.phase === 'done' ? Math.round(Math.min(0.6, speed.result.mbps / 330) * 100) : null
	return (
		<div className={`speedo ${speed.phase}`} role="status" aria-live="polite">
			<div className="lbl">{running && <i className="blink">▶</i>} {label}</div>
			<div className="bar"><i style={{ width: `${Math.round(fraction * 100)}%` }} /></div>
			<div className="num">
				<b>{mbps ? mbps.toFixed(mbps >= 100 ? 0 : 1) : '---'}</b> <span>MBPS</span>
				{turbo !== null && <em> · TURBO +{turbo}%{speed.phase === 'done' && speed.record ? ' · NEW RECORD' : ''}</em>}
			</div>
		</div>
	)
}

export function App() {
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const sceneRef = useRef<ArcadeScene | null>(null)
	const runnerRef = useRef<GameRunner | null>(null)
	const [info, setInfo] = useState<IPInfo | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [other, setOther] = useState<{ family: IPFamily; ip: string | null } | null>(null)
	const [family, setFamily] = useState<IPFamily>('ipv4')
	const [god, setGod] = useState(false)
	const [attract, setAttract] = useState(false)
	const [muted, setMuted] = useState(audio.muted)
	const [modal, setModal] = useState<Modal>(null)
	const [status, setStatus] = useState('Loading… please wait')
	const [toasts, setToasts] = useState<Toast[]>([])
	const [game, setGame] = useState<RunnerStatus | null>(null)
	const [speed, setSpeed] = useState<Speed | null>(null)
	const [speedHi, setSpeedHi] = useState(() => readNum('wibble:hiscore'))
	const [gameHi, setGameHi] = useState<Record<GameKind, number>>(() => ({ snake: readNum('wibble:hi:snake'), invaders: readNum('wibble:hi:invaders'), dash: readNum('wibble:hi:dash') }))
	const [display, setDisplay] = useState('LOADING')
	const [logoState, setLogoState] = useState<'idle' | 'melt' | 'reform'>('idle')
	const [crt, setCrt] = useState<CrtState | null>(null)
	const crtScreen = useRef<CrtScreen | null>(null)
	/** Which game the CRT is showing; null while it is off or melting away. */
	const crtGame = useRef<GameKind | null>(null)
	const lastFrame = useRef<Frame | null>(null)
	const live = useLive()
	const idleTimer = useRef(0)
	const attractTimer = useRef(0)
	const gameOverTimer = useRef(0)
	const unlocked = useRef(false)
	// refs so the runner hooks (created once) always see fresh values
	const attractRef = useRef(false)
	const ipRef = useRef<string | null>(null)
	const gameHiRef = useRef(gameHi)
	gameHiRef.current = gameHi

	// ---- helpers ----
	const toast = useCallback((text: string, big = false) => {
		const id = Date.now() + Math.random()
		setToasts((t) => [...t.slice(-3), { id, text, big }])
		setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), big ? 2200 : 1800)
	}, [])

	const ips = useMemo(() => {
		const m: Partial<Record<IPFamily, string | null>> = {}
		if (info) m[info.family] = info.ip
		if (other) m[other.family] = other.ip
		return m
	}, [info, other])
	const ip = ips[family] ?? null
	ipRef.current = ip

	const showIP = useCallback(() => { if (ipRef.current) setDisplay(ipRef.current) }, [])

	/** Any click or key counts as a user gesture: unlock audio once, reset the idle timer. */
	const poke = useCallback(() => {
		if (!unlocked.current) { unlocked.current = true; audio.unlock() }
		clearTimeout(idleTimer.current)
		idleTimer.current = window.setTimeout(() => setAttract(true), IDLE_MS)
	}, [])

	// ---- boot: scene + game runner ----
	useEffect(() => {
		if (!canvasRef.current) return
		const scene = new ArcadeScene(canvasRef.current)
		sceneRef.current = scene
		const runner = new GameRunner({
			frame: (cells, w, h, mode) => {
				if (mode !== 'play') { scene.setGrid(cells, w, h); return }
				lastFrame.current = { cells, W: w, H: h }
				crtScreen.current?.draw(cells, w, h)
			},
			status: (s) => {
				if (s.mode === 'play' && crtGame.current !== s.kind) {
					// someone is playing: the CRT melts in around the board (or just swaps label if it's already on)
					const g = runner.current!
					const fresh = crtGame.current === null
					crtGame.current = s.kind
					if (fresh) {
						scene.clear()
						runner.hold(REDUCE_MOTION ? 0 : CRT_MELT_MS + CRT_WARM_MS)
					}
					setCrt((c) => ({ kind: s.kind, W: g.W, H: g.H, phase: fresh || !c ? 'in' : c.phase }))
				}
				setGame({ ...s })
			},
			event: (e) => {
				switch (e) {
					case 'eat': audio.eat(); break
					case 'shoot': audio.shoot(); break
					case 'hit': audio.blip(); break
					case 'jump': audio.tick(); break
					case 'clear': break
					case 'wave': audio.fanfare(); break
					case 'lose-life': audio.error(); break
					case 'die': audio.explode(); break
				}
			},
			ended: (s, reason) => {
				setGame(null)
				clearTimeout(gameOverTimer.current)
				if (crtGame.current) {
					crtGame.current = null
					setCrt((c) => (c ? { ...c, phase: 'out' } : c))
				}
				if (s.mode === 'play') {
					const hi = gameHiRef.current[s.kind]
					if (s.score > hi) {
						writeNum(`wibble:hi:${s.kind}`, s.score)
						setGameHi((g) => ({ ...g, [s.kind]: s.score }))
						if (reason === 'over') toast('New hi-score!', true)
					}
					setDisplay(reason === 'over' ? `GAME OVER\n${s.score}` : `${s.score} PTS`)
					setStatus(`${GAME_LABEL[s.kind]} · ${s.score} pts · hi ${Math.max(hi, s.score)}`)
					gameOverTimer.current = window.setTimeout(() => { setDisplay(ipRef.current ?? 'WIBBLE'); setStatus('Player 1 · free play · press ? for help'); setSpeed(null) }, 3500)
				} else if (attractRef.current) {
					// demo finished: roll on to the next act
					const next = DEMO_ORDER[(DEMO_ORDER.indexOf(s.kind) + 1) % DEMO_ORDER.length]
					setDisplay(reason === 'over' ? 'GAME OVER' : ipRef.current ?? 'WIBBLE')
					attractTimer.current = window.setTimeout(() => runDemo(next), s.kind === 'dash' ? 4000 : 1500)
				} else showIP()
			},
		})
		runnerRef.current = runner
		return () => { runner.dispose(); scene.dispose() }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	/** Start a demo act (used by the attract loop). Defined outside the effect so `ended` can recurse. */
	const runDemo = (kind: GameKind) => {
		if (!attractRef.current || !runnerRef.current) return
		setStatus(`Demo · ${GAME_LABEL[kind]} · ${GAME_HINT[kind]} to take over`)
		runnerRef.current.start(kind, 'demo', DEMO_MAX_MS[kind])
	}

	useEffect(() => { if (!game) sceneRef.current?.setText(display) }, [display, game])

	// CRT phases: melt in → picture on; melt out → gone
	useEffect(() => {
		if (!crt || crt.phase === 'on') return
		const ms = REDUCE_MOTION ? 0 : crt.phase === 'in' ? CRT_MELT_MS : CRT_OUT_MS
		const t = setTimeout(() => setCrt((c) => (c?.phase === 'in' ? { ...c, phase: 'on' } : c?.phase === 'out' ? null : c)), ms)
		return () => clearTimeout(t)
	}, [crt?.phase])

	/** The CRT canvas mounts after the first frame has already gone out, so replay it. */
	const attachCrt = useCallback((screen: CrtScreen | null) => {
		crtScreen.current = screen
		const f = lastFrame.current
		if (screen && f) screen.draw(f.cells, f.W, f.H)
	}, [])
	useEffect(() => { sceneRef.current?.setGodMode(god) }, [god])

	// attract loop: IP spins for a bit, then the games demo themselves
	useEffect(() => {
		attractRef.current = attract
		sceneRef.current?.setAttract(attract)
		const runner = runnerRef.current
		if (attract) {
			if (!muted) audio.startMusic()
			if (!runner?.active) {
				showIP()
				setStatus('Attract mode · press any key to stop')
				attractTimer.current = window.setTimeout(() => runDemo(DEMO_ORDER[0]), 600)
			}
		} else {
			audio.stopMusic()
			clearTimeout(attractTimer.current)
			if (runner?.active && runner.status?.mode === 'demo') { runner.stop(); setGame(null); showIP() }
		}
		return () => clearTimeout(attractTimer.current)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [attract])

	// the person grabbed the controls mid-demo: attract is over, the game carries on
	useEffect(() => {
		if (game?.mode === 'play' && attractRef.current) { setAttract(false); setStatus(`${GAME_LABEL[game.kind]} · ${GAME_HINT[game.kind]}`) }
	}, [game?.mode, game?.kind])

	useEffect(() => {
		let cancelled = false
		fetchInfo()
			.then(async (i) => {
				if (cancelled) return
				setInfo(i)
				setFamily(i.family)
				setDisplay(i.ip)
				setStatus('Player 1 · free play · press ? for help')
				audio.start()
				const o = await fetchOtherFamily(i.family)
				if (!cancelled) setOther({ family: i.family === 'ipv4' ? 'ipv6' : 'ipv4', ip: o })
			})
			.catch((e: Error) => {
				if (cancelled) return
				setError(e.message)
				setDisplay('GAME OVER')
				setStatus('Could not reach the edge. Refresh to continue.')
			})
		idleTimer.current = window.setTimeout(() => setAttract(true), IDLE_MS)
		const onAny = () => { poke() }
		addEventListener('pointerdown', onAny)
		return () => { cancelled = true; clearTimeout(idleTimer.current); removeEventListener('pointerdown', onAny) }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// new challenger toasts
	const lastJoinT = useRef(0)
	useEffect(() => {
		if (!live.lastJoin || live.lastJoin.t === lastJoinT.current) return
		lastJoinT.current = live.lastJoin.t
		toast(`New challenger: ${up(live.lastJoin.city, 'somewhere')} ${live.lastJoin.country}`)
		audio.blip()
	}, [live.lastJoin, toast])

	// ---- actions ----
	const startGame = useCallback((kind: GameKind) => {
		setAttract(false)
		clearTimeout(gameOverTimer.current)
		setModal(null)
		audio.start()
		setStatus(`${GAME_LABEL[kind]} · ${GAME_HINT[kind]} · esc quits`)
		runnerRef.current?.start(kind, 'play')
	}, [])

	const quitGame = useCallback(() => runnerRef.current?.quit(), [])

	const toggleFamily = useCallback(() => {
		if (!info) return
		const next: IPFamily = family === 'ipv4' ? 'ipv6' : 'ipv4'
		const target = ips[next]
		if (target === undefined) { toast('Still checking…'); return }
		if (target === null) { audio.error(); setDisplay(`NO ${next.toUpperCase()}`); toast(`No ${next} route from here`, true); setTimeout(showIP, 1800); return }
		audio.start()
		setFamily(next)
		setDisplay(target)
		setStatus(`Stage ${next === 'ipv6' ? 2 : 1} · ${next.toUpperCase()}`)
	}, [info, family, ips, toast, showIP])

	const copy = useCallback(async () => {
		if (!ip) return
		try { await navigator.clipboard.writeText(ip); audio.powerUp(); toast(`Copied ${ip}`) } catch { audio.error(); toast('Clipboard blocked') }
	}, [ip, toast])

	const share = useCallback(async () => {
		if (!ip) return
		const data = { title: 'wibble.foo', text: `My IP is ${ip} — found out the loud way at wibble.foo`, url: 'https://wibble.foo' }
		if (navigator.share) { try { await navigator.share(data) } catch { /* dismissed */ } }
		else { try { await navigator.clipboard.writeText(`${data.text} ${data.url}`); toast('Share text copied') } catch { toast('Clipboard blocked') } }
	}, [ip, toast])

	/** Bonus stage: the endless runner, with a real download test running underneath. Your Mbps becomes turbo. */
	const bonusStage = useCallback(async () => {
		startGame('dash')
		setStatus('Bonus stage · speed test running · your Mbps becomes turbo')
		setSpeed({ phase: 'ping' })
		audio.fanfare()
		try {
			const result = await speedTest(SPEED_BYTES, (fraction, mbps) => setSpeed({ phase: 'download', fraction, mbps }))
			setSpeed({ phase: 'done', result, record: result.mbps > speedHi })
			const dash = runnerRef.current?.current
			if (dash?.name === 'dash') (dash as Dash).setBandwidth(result.mbps)
			setStatus(`Bonus stage · ${result.mbps} Mbps · turbo engaged · ${GAME_HINT.dash}`)
			toast(`${result.mbps} Mbps · turbo!`, true)
			audio.powerUp()
			if (result.mbps > speedHi) { setSpeedHi(result.mbps); writeNum('wibble:hiscore', result.mbps) }
		} catch {
			setSpeed({ phase: 'failed' })
			toast('Speed test failed · running on fumes')
		}
	}, [startGame, speedHi, toast])

	const closeModal = useCallback(() => {
		setModal(null)
		if (!runnerRef.current?.active) showIP()
	}, [showIP])

	useKonami(useCallback(() => {
		setGod(true)
		audio.fanfare()
		toast('God mode', true)
		setStatus('God mode · rainbow voxels')
	}, [toast]))

	// keyboard: games first, then the cabinet shortcuts
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.metaKey || e.ctrlKey || e.altKey) return
			if (e.target instanceof HTMLInputElement) return
			poke()
			if (runnerRef.current?.keyDown(e)) { e.preventDefault(); return }
			if (attractRef.current && !['Shift'].includes(e.key)) setAttract(false)
			switch (e.key) {
				case '1': toggleFamily(); break
				case '2': startGame('snake'); break
				case '3': startGame('invaders'); break
				case '4': void bonusStage(); break
				case 'c': case 'C': void copy(); break
				case 'm': case 'M': setMuted((m) => { audio.setMuted(!m); return !m }); break
				case '?': setModal((m) => (m === 'help' ? null : 'help')); break
				case 'Escape': closeModal(); break
				case '`': location.href = '/tty'; break
			}
		}
		const onKeyUp = (e: KeyboardEvent) => runnerRef.current?.keyUp(e)
		addEventListener('keydown', onKey)
		addEventListener('keyup', onKeyUp)
		return () => { removeEventListener('keydown', onKey); removeEventListener('keyup', onKeyUp) }
	}, [toggleFamily, copy, closeModal, startGame, bonusStage, poke])

	// stop attract on any click
	useEffect(() => {
		const onDown = () => { if (attractRef.current && !runnerRef.current?.active) setAttract(false) }
		addEventListener('pointerdown', onDown)
		return () => removeEventListener('pointerdown', onDown)
	}, [])

	// the logo melts away while a game has the board, and reforms afterwards
	const playingNow = game !== null
	useEffect(() => {
		if (playingNow) setLogoState('melt')
		else setLogoState((st) => (st === 'melt' ? 'reform' : st))
	}, [playingNow])
	useEffect(() => {
		if (logoState !== 'reform') return
		const t = setTimeout(() => setLogoState('idle'), 900)
		return () => clearTimeout(t)
	}, [logoState])

	// ---- render ----
	const w = info?.weather
	const rtt = info?.clientTcpRtt
	const playing = game !== null
	const hiLine = game ? `${GAME_LABEL[game.kind].toUpperCase()} ${Math.max(gameHi[game.kind], game.score)}` : speedHi ? `${speedHi.toFixed(0)} MBPS` : rtt !== undefined ? `${pad2(rtt)} MS` : '------'
	const scoreLine = game
		? `${game.mode === 'demo' ? 'DEMO · ' : ''}${game.score} PTS${game.lives !== undefined ? ` · ${'♥'.repeat(Math.max(0, game.lives))}` : ''}${game.wave && game.wave > 1 ? ` · WAVE ${game.wave}` : ''}${game.kind === 'dash' && speed?.phase === 'done' ? ` · ${speed.result.mbps} MBPS` : ''}`
		: null
	const tickerItems = info
		? [
				`your ip is ${info.ip}`,
				info.ptr ? `reverse dns ${info.ptr}` : null,
				`served by cloudflare ${info.colo ?? '???'}${rtt !== undefined ? ` in ${rtt}ms` : ''}`,
				`${info.httpProtocol ?? 'http'} over ${info.tlsVersion ?? 'tls'}`,
				w ? `${w.label.toLowerCase()} ${w.temperatureC}°c in ${info.city ?? 'your city'}` : null,
				live.online !== null ? `${live.online} player${live.online === 1 ? '' : 's'} online` : null,
				'free play · snake · invaders · bonus stage',
				'curl wibble.foo for plain text',
				'/json for robots · /badge.svg for readmes',
				'press ` for the terminal',
				'↑↑↓↓←→←→BA',
				'no logs · no cookies · no ads',
			].filter(Boolean)
		: ['booting…']

	return (
		<>
			<canvas id="gl" ref={canvasRef} aria-label="3D voxel rendering of your public IP address over a synthwave grid" />
			<div className={`dim${crt ? ' on' : ''}`} />
			<div className="scan" />
			<span className="sr" aria-live="polite">{ip ? `Your public IP is ${ip}` : status}</span>

			<div className={`hud${crt ? ' crt-up' : ''}`}>
				<div>
					<div className="row">
						<div>
							<span className="k">1UP</span> <span className="v">{flag(info?.country)} {up(info?.city, 'UNKNOWN')}·{up(info?.country, '??')}</span><br />
							<span className="k">ISP</span> <span className="v">{info?.asn ? `AS${info.asn} ` : ''}{up(info?.asOrganization?.slice(0, 22), '???')}</span>
							{scoreLine && <><br /><span className="k">SCORE</span> <span className="hi">{scoreLine}</span></>}
						</div>
						<div className="r3">
							<span className="k">HI-SCORE</span><br /><span className="hi">{hiLine}</span><br />
							<span className="k">PLAYERS</span> <span className="v">{live.online === null ? '--' : pad2(live.online)}</span>
						</div>
						<div className="right">
							<span className="k">EDGE</span> <span className="v">{up(info?.colo)}{rtt !== undefined ? ` ${rtt}MS` : ''}</span><br />
							<span className="k">CREDIT</span> <span className="v">{god ? '∞' : 'FREE PLAY'}</span>
							{w && <><br /><span className="k">WX</span> <span className="v">{w.emoji} {w.temperatureC}°C</span></>}
						</div>
					</div>
					<div className={`logo${god ? ' god' : ''}${logoState !== 'idle' ? ` ${logoState}` : ''}`}>
						{[...'WIBBLE'].map((ch, i) => <span key={i} style={{ animationDelay: `${i * 0.07}s` }}>{ch}</span>)}
					</div>
					{ip && (playing || display !== ip) && <IPLine ip={ip} />}
				</div>

				<div className="stage">
					{crt && <Crt {...crt} attach={attachCrt} />}
				</div>

				<div className="bottom">
					{speed && <Speedo speed={speed} />}
					<div className={`press${error ? ' err' : ''}${playing ? ' steady' : ''}`}>{status}</div>
					<div className="btns">
						{playing
							? <button className="coin" onClick={quitGame} title="Esc">Quit game</button>
							: <>
								<button className="coin" onClick={() => startGame('snake')} disabled={!info} title="2">Snake</button>
								<button className="coin" onClick={() => startGame('invaders')} disabled={!info} title="3">Invaders</button>
								<button className="coin" onClick={bonusStage} disabled={!info} title="4">Bonus Stage</button>
							</>}
						<button onClick={copy} disabled={!ip} title="C">Copy IP</button>
						<button onClick={toggleFamily} disabled={!info || playing} aria-pressed={family === 'ipv6'} title="1">{family === 'ipv4' ? 'IPv6' : 'IPv4'}</button>
						<button onClick={share} disabled={!ip}>Share</button>
						<button onClick={() => setModal('json')} disabled={!info}>JSON</button>
						<button onClick={() => setAttract((a) => !a)} aria-pressed={attract} disabled={playing && game?.mode === 'play'}>Attract</button>
						<button onClick={() => setMuted((m) => { audio.setMuted(!m); return !m })} aria-pressed={!muted} title="M">{muted ? 'Sound Off' : 'Sound On'}</button>
						<a className="btn" href="/tty" title="`">TTY</a>
						<button onClick={() => setModal('help')} title="?">?</button>
					</div>
				</div>
			</div>

			<div className="ticker" aria-hidden="true"><span>{tickerItems.map((t, i) => <i key={i}><b>★</b> {t} </i>)}</span></div>

			<div className="toasts" aria-live="polite">
				{toasts.map((t) => <div key={t.id} className={`toast${t.big ? ' big' : ''}`}>{t.text}</div>)}
			</div>

			{modal && (
				<div className="modal-backdrop" onClick={closeModal}>
					<div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
						{modal === 'json' && <>
							<header><span>GET /json</span><button onClick={closeModal}>Close</button></header>
							<pre>{JSON.stringify(info, null, 2)}</pre>
						</>}
						{modal === 'help' && <>
							<header><span>How to play</span><button onClick={closeModal}>Close</button></header>
							<div className="help">
								<dl>
									<dt>2 / 3 / 4</dt><dd>Snake · Invaders · Bonus Stage (endless runner; your download speed becomes turbo)</dd>
									<dt>arrows · WASD</dt><dd>steer · move · jump. Swipe on touch.</dd>
									<dt>space</dt><dd>fire · jump. Tap on touch.</dd>
									<dt>esc</dt><dd>quit the game</dd>
									<dt>1</dt><dd>switch IPv4 / IPv6</dd>
									<dt>C</dt><dd>copy address</dd>
									<dt>M</dt><dd>mute</dd>
									<dt>`</dt><dd>open the terminal</dd>
									<dt>↑↑↓↓←→←→BA</dt><dd>you know what this does</dd>
								</dl>
								<p className="note">Leave it alone for 90 seconds and the cabinet plays itself. Press anything during a demo to take over.</p>
								<h4>For robots</h4>
								<pre>{`curl wibble.foo          # plain text ip
curl wibble.foo/json     # everything
curl wibble.foo/v6       # only over ipv6
curl wibble.foo/ptr      # reverse dns
curl wibble.foo/weather  # where you are
curl wibble.foo/haiku    # workers ai poetry (tty only)
![ip](https://wibble.foo/badge.svg)`}</pre>
							</div>
						</>}
					</div>
				</div>
			)}
		</>
	)
}
